import React, { useState, useEffect, useCallback } from "react";
import "./CallBrief.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lane-discipline line — always on the Call Brief, never edited
const LANE_DISCIPLINE = "Moovin Forward does not engage in any aspect of the leasing function. The scope terminates at delivery of an inspection-ready unit. The leasing pipeline remains entirely within the management agent's authority. No TREC overlap, no operational interference.";

// Anticipated objections + Cushman-tier scripted responses
const OBJECTIONS = [
  {
    q: '"We have an in-house turn team."',
    a: "Of course. The proposal is not to displace in-house. The proposal is to layer a specialty refresh on the slowest-absorbing floor plan, where the concession depth indicates the standard turn is not closing the positioning gap. In-house continues to handle the broader make-ready calendar."
  },
  {
    q: '"We can\'t pay 50% upfront — it has to go through Yardi/RealPage Net 30."',
    a: "Understood. The first 50% is classified as a Material Mobilization Fee — pre-paid materials, not labor advance. The classification routes the invoice to manual review against pre-paid materials terms, which sits within most institutional AP authorization frameworks. It is a different vendor category, not a policy deviation."
  },
  {
    q: '"What about Section 8 / voucher coordination?"',
    a: "Out of scope. Moovin Forward does not prepare voucher applications, coordinate with housing authorities on tenant placement, or interact with the leasing pipeline. Our scope is limited to the physical condition of the unit. Voucher coordination remains with your licensed PM agent."
  },
  {
    q: '"Send me a proposal and we\'ll review it."',
    a: "Happy to. The proposal is more accurate if we walk the units together first — seventy-two-hour turnaround on a unit-level scope and budget at no cost. The deliverable is the same; the accuracy is significantly higher with an on-site walk."
  }
];

function fmtCurrency(n) {
  if (n == null || isNaN(n)) return "—";
  return "$" + Math.round(n).toLocaleString();
}

const StatField = ({ label, value, onChange, type = "text", prefix, suffix, testid }) => (
  <div className="cb-stat-field">
    <label className="cb-stat-label">{label}</label>
    <div className="cb-stat-input-wrap">
      {prefix && <span className="cb-stat-affix">{prefix}</span>}
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value)}
        className="cb-stat-input"
        data-testid={testid}
      />
      {suffix && <span className="cb-stat-affix">{suffix}</span>}
    </div>
  </div>
);

const CallBrief = ({ prospect, onClose }) => {
  const [stats, setStats] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [outcome, setOutcome] = useState("logged");
  const [saving, setSaving] = useState(false);
  const [statsSaving, setStatsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const pid = prospect.prospect_id;

  // Load on open
  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${BACKEND_URL}/api/admin/prospects/${pid}/live-stats`, { credentials: "include" }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/admin/prospects/${pid}/call-notes`, { credentials: "include" }).then(r => r.json())
    ]).then(([s, n]) => {
      if (!active) return;
      setStats(s || { prospect_id: pid });
      setNotes(n.notes || []);
    }).catch(() => {});
    return () => { active = false; };
  }, [pid]);

  // Debounced auto-save of stats
  const persistStats = useCallback(async (next) => {
    setStatsSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/prospects/${pid}/live-stats`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next)
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setLastSaved(new Date());
      }
    } finally {
      setStatsSaving(false);
    }
  }, [pid]);

  const updateStat = (key, value) => {
    const next = { ...stats, [key]: value };
    setStats(next);
    // debounce
    if (window._cbSaveTimer) clearTimeout(window._cbSaveTimer);
    window._cbSaveTimer = setTimeout(() => persistStats({ [key]: value }), 600);
  };

  const saveNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/prospects/${pid}/call-notes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: newNote.trim(), outcome })
      });
      if (res.ok) {
        const note = await res.json();
        setNotes([note, ...notes]);
        setNewNote("");
        setOutcome("logged");
      }
    } finally {
      setSaving(false);
    }
  };

  // ====== Computed Bleed Math (recalculates as Shannon edits) ======
  const v = stats || {};
  const tfpVacancies = Number(v.target_floorplan_vacancies) || 0;
  const rentLow = Number(v.in_place_rent_low) || 0;
  const rentHigh = Number(v.in_place_rent_high) || 0;
  const midpoint = rentLow && rentHigh ? (rentLow + rentHigh) / 2 : rentLow || rentHigh || 0;
  const monthsFree = Number(v.concession_months_free) || 0;
  const locatorRebate = Number(v.locator_rebate_per_door) || 0;
  const thirdPartyPct = Number(v.third_party_lease_pct) || 0;

  const concessionBleed = tfpVacancies * midpoint * monthsFree;
  const locatorBleed = tfpVacancies * (thirdPartyPct / 100) * locatorRebate;
  // Deposit waiver risk reserve: assume Jetty-style 8% claim rate × 0.5 month rent reserve
  const depositReserve = v.deposit_program ? tfpVacancies * midpoint * 0.5 * 0.08 : 0;
  const totalBleed = concessionBleed + locatorBleed + depositReserve;
  const annualBleed = totalBleed * 12;

  if (!stats) {
    return (
      <div className="cb-overlay" onClick={onClose}>
        <div className="cb-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cb-loading">Loading call brief…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cb-overlay" onClick={onClose} data-testid="call-brief-overlay">
      <div className="cb-modal" onClick={(e) => e.stopPropagation()} data-testid={`call-brief-${pid}`}>
        {/* HEADER */}
        <div className="cb-header">
          <div>
            <div className="cb-eyebrow">Call Brief</div>
            <h2 className="cb-company">{prospect.company}</h2>
            <div className="cb-meta">
              {prospect.units > 0 && <span>{prospect.units.toLocaleString()} units</span>}
              <span>{prospect.address}</span>
            </div>
          </div>
          <button onClick={onClose} className="cb-close" data-testid="call-brief-close" aria-label="Close">✕</button>
        </div>

        {/* CONTACTS — tap-to-call */}
        <div className="cb-section cb-contacts">
          <div className="cb-section-title">Contacts</div>
          <div className="cb-contact-grid">
            <div className="cb-contact-card">
              <StatField label="Primary Name" value={v.primary_contact_name} onChange={(x) => updateStat("primary_contact_name", x)} testid="cb-primary-name" />
              <StatField label="Role" value={v.primary_contact_role} onChange={(x) => updateStat("primary_contact_role", x)} testid="cb-primary-role" />
              {v.primary_contact_phone && (
                <a href={`tel:${v.primary_contact_phone}`} className="cb-call-btn" data-testid="cb-primary-call">
                  <i className="fas fa-phone"></i> Call {v.primary_contact_phone}
                </a>
              )}
              <StatField label="Phone" value={v.primary_contact_phone} onChange={(x) => updateStat("primary_contact_phone", x)} testid="cb-primary-phone" />
              {v.primary_contact_email && (
                <a href={`mailto:${v.primary_contact_email}`} className="cb-email-btn" data-testid="cb-primary-email-link">
                  <i className="fas fa-envelope"></i> {v.primary_contact_email}
                </a>
              )}
              <StatField label="Email" value={v.primary_contact_email} onChange={(x) => updateStat("primary_contact_email", x)} testid="cb-primary-email" />
            </div>
            <div className="cb-contact-card">
              <StatField label="Secondary Name" value={v.secondary_contact_name} onChange={(x) => updateStat("secondary_contact_name", x)} testid="cb-secondary-name" />
              <StatField label="Role" value={v.secondary_contact_role} onChange={(x) => updateStat("secondary_contact_role", x)} testid="cb-secondary-role" />
              {v.secondary_contact_phone && (
                <a href={`tel:${v.secondary_contact_phone}`} className="cb-call-btn cb-call-btn-secondary" data-testid="cb-secondary-call">
                  <i className="fas fa-phone"></i> Call {v.secondary_contact_phone}
                </a>
              )}
              <StatField label="Phone" value={v.secondary_contact_phone} onChange={(x) => updateStat("secondary_contact_phone", x)} testid="cb-secondary-phone" />
              {v.secondary_contact_email && (
                <a href={`mailto:${v.secondary_contact_email}`} className="cb-email-btn" data-testid="cb-secondary-email-link">
                  <i className="fas fa-envelope"></i> {v.secondary_contact_email}
                </a>
              )}
              <StatField label="Email" value={v.secondary_contact_email} onChange={(x) => updateStat("secondary_contact_email", x)} testid="cb-secondary-email" />
            </div>
          </div>
        </div>

        {/* LIVE STATS — editable, auto-saves */}
        <div className="cb-section">
          <div className="cb-section-title-row">
            <div className="cb-section-title">Live Stats <span className="cb-section-hint">(edit on the call — math updates instantly)</span></div>
            <div className="cb-save-indicator">{statsSaving ? "Saving…" : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : ""}</div>
          </div>
          <div className="cb-stat-grid">
            <StatField label="Total Vacancies" value={v.total_vacancies} onChange={(x) => updateStat("total_vacancies", x)} type="number" testid="cb-total-vacancies" />
            <StatField label="Target Floor Plan" value={v.target_floorplan} onChange={(x) => updateStat("target_floorplan", x)} testid="cb-target-floorplan" />
            <StatField label="Vacant in Floor Plan" value={v.target_floorplan_vacancies} onChange={(x) => updateStat("target_floorplan_vacancies", x)} type="number" testid="cb-target-fp-vac" />
            <StatField label="In-Place Rent (Low)" value={v.in_place_rent_low} onChange={(x) => updateStat("in_place_rent_low", x)} type="number" prefix="$" testid="cb-rent-low" />
            <StatField label="In-Place Rent (High)" value={v.in_place_rent_high} onChange={(x) => updateStat("in_place_rent_high", x)} type="number" prefix="$" testid="cb-rent-high" />
            <StatField label="Free Months Concession" value={v.concession_months_free} onChange={(x) => updateStat("concession_months_free", x)} type="number" suffix="mo" testid="cb-months-free" />
            <StatField label="Locator Rebate / Door" value={v.locator_rebate_per_door} onChange={(x) => updateStat("locator_rebate_per_door", x)} type="number" prefix="$" testid="cb-locator-rebate" />
            <StatField label="% Leased via 3rd Party" value={v.third_party_lease_pct} onChange={(x) => updateStat("third_party_lease_pct", x)} type="number" suffix="%" testid="cb-third-party-pct" />
            <StatField label="Submarket Rent Gap" value={v.submarket_rent_gap_pct} onChange={(x) => updateStat("submarket_rent_gap_pct", x)} type="number" suffix="%" testid="cb-rent-gap" />
            <StatField label="Deposit Program" value={v.deposit_program} onChange={(x) => updateStat("deposit_program", x)} testid="cb-deposit-program" />
          </div>
        </div>

        {/* PROPERTY INTEL — Why this property is failing (per Shannon's Weststone example) */}
        <div className="cb-section" style={{ borderTop: "1px solid rgba(184,145,90,0.3)", paddingTop: 14, marginTop: 4 }}>
          <div className="cb-section-title" style={{ color: "#B8915A" }}>
            🔍 Property Intel <span className="cb-section-hint">(why this property is failing — auto-injects into pitches)</span>
          </div>
          <div className="cb-stat-grid">
            <StatField label="Total Units (Portfolio)" value={v.total_units_portfolio} onChange={(x) => updateStat("total_units_portfolio", x)} type="number" testid="cb-units-portfolio" />
            <StatField label="Loan Status" value={v.loan_status} onChange={(x) => updateStat("loan_status", x)} testid="cb-loan-status" />
            <StatField label="Loan Due Date" value={v.loan_due_date} onChange={(x) => updateStat("loan_due_date", x)} testid="cb-loan-due" />
            <StatField label="Turnover Rate" value={v.turnover_rate_pct} onChange={(x) => updateStat("turnover_rate_pct", x)} type="number" suffix="%" testid="cb-turnover-rate" />
            <StatField label="Intel Source" value={v.intel_source} onChange={(x) => updateStat("intel_source", x)} testid="cb-intel-source" />
          </div>
          <textarea
            className="cb-intel-textarea"
            value={v.why_failing || ""}
            onChange={(e) => updateStat("why_failing", e.target.value)}
            placeholder="Why this property is failing (e.g., 'Loan due Q4, 54 vacant in a 220-unit portfolio, high turnover from deferred maintenance, FOMO leases, third-party leasing churn'). One sentence per failure point."
            rows={3}
            data-testid="cb-why-failing"
            style={{ marginTop: 8 }}
          />
        </div>

        {/* BLEED MATH — auto-computed */}
        <div className="cb-section cb-bleed">
          <div className="cb-section-title">Concession Bleed (Target Floor Plan)</div>
          <div className="cb-bleed-grid">
            <div className="cb-bleed-item">
              <div className="cb-bleed-label">Free-month concession</div>
              <div className="cb-bleed-calc">{tfpVacancies} units × {fmtCurrency(midpoint)} × {monthsFree} mo</div>
              <div className="cb-bleed-value" data-testid="cb-bleed-concession">{fmtCurrency(concessionBleed)}</div>
            </div>
            <div className="cb-bleed-item">
              <div className="cb-bleed-label">Locator rebates</div>
              <div className="cb-bleed-calc">{tfpVacancies} × {thirdPartyPct}% × {fmtCurrency(locatorRebate)}</div>
              <div className="cb-bleed-value" data-testid="cb-bleed-locator">{fmtCurrency(locatorBleed)}</div>
            </div>
            <div className="cb-bleed-item">
              <div className="cb-bleed-label">Deposit waiver reserve</div>
              <div className="cb-bleed-calc">{v.deposit_program ? `${tfpVacancies} × 0.5mo × 8% claim` : "No program"}</div>
              <div className="cb-bleed-value" data-testid="cb-bleed-deposit">{fmtCurrency(depositReserve)}</div>
            </div>
            <div className="cb-bleed-item cb-bleed-total">
              <div className="cb-bleed-label">Monthly Drag</div>
              <div className="cb-bleed-calc">Annualized: {fmtCurrency(annualBleed)}</div>
              <div className="cb-bleed-value" data-testid="cb-bleed-total">{fmtCurrency(totalBleed)}</div>
            </div>
          </div>
        </div>

        {/* TALKING POINTS (from prospect record) */}
        {prospect.pain_point && (
          <div className="cb-section">
            <div className="cb-section-title">Opportunity</div>
            <p className="cb-text">{prospect.pain_point}</p>
          </div>
        )}
        {prospect.talking_points && (
          <div className="cb-section">
            <div className="cb-section-title">Approach</div>
            <p className="cb-text">{prospect.talking_points}</p>
          </div>
        )}

        {/* INTEL NOTES — Shannon's pre-call observations */}
        <div className="cb-section">
          <div className="cb-section-title">Intel Notes</div>
          <textarea
            className="cb-intel-textarea"
            value={v.intel_notes || ""}
            onChange={(e) => updateStat("intel_notes", e.target.value)}
            placeholder="Pre-call intel: framing, samples to bring, expected objections, anything you want to remember."
            rows={4}
            data-testid="cb-intel-notes"
          />
        </div>

        {/* LANE DISCIPLINE — always shown */}
        <div className="cb-section cb-lane-discipline">
          <div className="cb-section-title">Lane Discipline (say this)</div>
          <p className="cb-lane-text">{LANE_DISCIPLINE}</p>
        </div>

        {/* ANTICIPATED OBJECTIONS */}
        <div className="cb-section">
          <div className="cb-section-title">Anticipated Objections</div>
          <div className="cb-objections">
            {OBJECTIONS.map((o, i) => (
              <details key={i} className="cb-objection" data-testid={`cb-objection-${i}`}>
                <summary className="cb-objection-q">{o.q}</summary>
                <p className="cb-objection-a">{o.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* QUICK NOTE — log after call */}
        <div className="cb-section cb-note-section">
          <div className="cb-section-title">Quick Note (log after call)</div>
          <textarea
            className="cb-note-textarea"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="What was said? What's the next move?"
            rows={3}
            data-testid="cb-note-input"
          />
          <div className="cb-note-controls">
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="cb-outcome-select" data-testid="cb-outcome-select">
              <option value="logged">Logged</option>
              <option value="callback">Callback Scheduled</option>
              <option value="meeting_set">Meeting Set</option>
              <option value="walk_through_booked">Walk-Through Booked</option>
              <option value="not_interested">Not Interested</option>
              <option value="wrong_number">Wrong Number / No Answer</option>
            </select>
            <button onClick={saveNote} disabled={saving || !newNote.trim()} className="cb-note-save" data-testid="cb-save-note">
              {saving ? "Saving…" : "Save Note"}
            </button>
          </div>
        </div>

        {/* NOTE HISTORY */}
        {notes.length > 0 && (
          <div className="cb-section">
            <div className="cb-section-title">Call History</div>
            <div className="cb-notes-list">
              {notes.map((n) => (
                <div key={n.id} className="cb-note-item" data-testid={`cb-note-${n.id}`}>
                  <div className="cb-note-meta">
                    <span className={`cb-outcome cb-outcome-${n.outcome}`}>{n.outcome.replace(/_/g, " ")}</span>
                    <span className="cb-note-date">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <p className="cb-note-body">{n.note}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallBrief;
